import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import config from '../config';
import useTranslation from './useTranslation';
import authService from '../services/auth';

const Icon = ({ name, className }) => (
  <span className={`material-symbols-outlined ${className || ''}`}>
    {name}
  </span>
);

const PopupOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: transparent;
    display: ${props => props.$isOpen ? 'block' : 'none'};
    z-index: 1000;
`;

const PopupContent = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90dvh;
    max-width: 90vw;
    height: 70dvh;
    background-color: white;
    overflow-y: auto;
    z-index: 1001;
    display: ${props => props.$isOpen ? 'block' : 'none'};
    
    word-wrap: break-word;
    border-radius: 10px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
    pointer-events: auto;
`;

const Content = styled.div`
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    padding: 1rem;
`;

const Section = styled.div`
    width: 100%;
    margin-bottom: 2rem;
    background-color: ${props => props.$backgroundColor || 'transparent'};
    opacity: ${props => props.$opacity || 1};
    
`;

const Title = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
    color: var(--nody-black);
    line-height: 1.2;
`;

const Description = styled.div`
    font-size: 1rem;
    line-height: 1.5;
    color: var(--nody-black-75);

    p {
        color: var(--nody-black-75);
    }

    ul {
        padding-left: 1.5rem;
    }
`;

const PolicyPopup = ({ isOpen, onClose }) => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLang] = useState(localStorage.getItem('language') || config.languages.default);
  const [pageTitle, setPageTitle] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    const fetchSectionDetails = async (sectionId) => {
      try {
        const data = await authService.get(config.API_ENDPOINTS.getParagraph(sectionId, selectedLang));
        return data;
      } catch (error) {
        console.error(`Errore durante il recupero della sezione ${sectionId}:`, error);
        return null;
      }
    };

    const fetchPolicyContent = async () => {
      try {
        const data = await authService.get(config.API_ENDPOINTS.getPageList(selectedLang));
        const policyPage = data.find(page => page.field_tipologia_pagina?.[0]?.value === 'informativa');
        
        if (policyPage) {
          setPageTitle(policyPage.title?.[0]?.value || '');
          
          if (policyPage.field_sezioni) {
            const sectionsDetails = await Promise.all(
              policyPage.field_sezioni.map(section => 
                fetchSectionDetails(section.target_id)
              )
            );
            
            setSections(sectionsDetails.filter(Boolean));
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Errore durante il recupero dei contenuti:', error);
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchPolicyContent();
    }
  }, [isOpen, selectedLang]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleTouchMove = (event) => {
    event.stopPropagation();
  };

  if (!isOpen) return null;

  return (
    <>
      <PopupOverlay $isOpen={isOpen} onClick={onClose} />
      <PopupContent $isOpen={isOpen} onTouchMove={handleTouchMove}>
        <div className="sticky-top bg-white border-bottom">
          <div className="container d-flex justify-content-between align-items-center py-3">
            <button
              onClick={onClose}
              aria-label="Chiudi"
              className="btn bg-white d-flex align-items-center justify-content-center ratio-1x1 goback"
              style={{ position: 'relative', top: 0, left: 0 }}
            >
              <Icon name="close" />
            </button>
          </div>
        </div>

        <Content>
          <h1 className="mb-4 mt-4">{pageTitle}</h1>
          {loading ? (
            <div>Caricamento...</div>
          ) : sections.length === 0 ? (
            <div>Nessun contenuto trovato</div>
          ) : (
            sections.map((section, index) => (
              <Section 
                key={section.id.value}
                $backgroundColor={section.field_colore_sezione?.color}
                $opacity={section.field_colore_sezione?.opacity}
              >
                {section.field_titolo_sezione && (
                  <Title>{section.field_titolo_sezione.value}</Title>
                )}
                {section.field_testo_sezione && (
                  <Description 
                    dangerouslySetInnerHTML={{ 
                      __html: section.field_testo_sezione.value 
                    }}
                  />
                )}
              </Section>
            ))
          )}
        </Content>
      </PopupContent>
    </>
  );
};

export default PolicyPopup; 